import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import { useTranslation } from "react-i18next";
import { dayLabel, formatTime } from "./helpers";

const GREEN = "#1f7a55";
const GREEN_BG = "#e3f5ec";
const GREEN_CHIP = "#cdeedd";

const StatRow = ({ label, value, positive }) => (
  <>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ py: 1.25 }}
    >
      <Typography sx={{ color: "#5a5a5a" }}>{label}</Typography>
      <Typography sx={{ fontWeight: 700, color: positive ? GREEN : "#2b2b2b" }}>
        {value}
      </Typography>
    </Stack>
    <Divider />
  </>
);

const HappyHourRecap = ({ open, onClose, recap, onRemind }) => {
  const { t } = useTranslation();
  if (!recap) return null;

  const bonus = recap.bonusCoinsEarned ?? recap.happyHourBonus ?? 0;
  const hours = recap.windowHours || 0;
  const hasNext = recap.nextStartTime != null;
  const nextMultiplier = recap.nextMultiplier || 2;
  const nextWhen = hasNext
    ? `${dayLabel(recap.nextStartTime, t)} ${formatTime(recap.nextStartTime)}`
    : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 10101 }}
      PaperProps={{
        sx: { borderRadius: 4, overflow: "hidden", background: "#fff" },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", top: 8, right: 8, color: "#9a9a9a", zIndex: 2 }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        {/* Result hero */}
        <Box
          sx={{ background: GREEN_BG, textAlign: "center", px: 3, py: 3.5, m: 2, borderRadius: 3 }}
        >
          <Typography
            sx={{ color: GREEN, fontWeight: 800, fontSize: 12, letterSpacing: 1.2 }}
          >
            {t("happyHour.recapKicker")}
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 22, color: "#1f1f1f" }}>
            {recap.message || t("happyHour.recapDefaultMessage")}
          </Typography>
          <Typography
            sx={{ color: GREEN, fontWeight: 900, fontSize: 48, lineHeight: 1.1 }}
          >
            +{bonus}
          </Typography>
          <Typography sx={{ color: "#5a5a5a", fontSize: 13 }}>
            {t("happyHour.recapBonusCoinsEarned")}
            {hours > 0 ? t("happyHour.recapBonusCoinsHoursSuffix", { hours }) : ""}
          </Typography>
        </Box>

        {/* Stats */}
        <Box sx={{ px: 3 }}>
          <Divider />
          <StatRow
            label={t("happyHour.recapChallengesPlayed")}
            value={recap.challengesPlayed ?? 0}
          />
          <StatRow
            label={t("happyHour.recapBaseCoins")}
            value={`+${recap.baseCoins ?? 0}`}
            positive
          />
          <StatRow
            label={t("happyHour.recapHappyHourBonus")}
            value={`+${recap.happyHourBonus ?? bonus}`}
            positive
          />
          <StatRow
            label={t("happyHour.recapStreakProgress")}
            value={t("happyHour.recapStreakProgressValue", {
              days: recap.streakProgress ?? 0,
            })}
            positive
          />
        </Box>

        {/* Next happy hour */}
        {hasNext && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ background: GREEN_BG, borderRadius: 2, px: 2, py: 1.25 }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  background: GREEN_CHIP,
                  color: GREEN,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <NotificationsActiveRoundedIcon fontSize="small" />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: "#2b2b2b", lineHeight: 1.2 }}>
                  {t("happyHour.recapNextHappyHour", { when: nextWhen })}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#7a7a7a" }}>
                  {t("happyHour.recapNextRewards", {
                    multiplier: nextMultiplier,
                  })}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}

        <Box sx={{ px: 3, py: 2.5 }}>
          <Button
            fullWidth
            onClick={() => {
              if (onRemind)
                onRemind(
                  hasNext ? `${nextWhen}` : t("happyHour.recapRemindNext")
                );
              onClose();
            }}
            sx={{
              background: GREEN,
              color: "#fff",
              fontWeight: 800,
              textTransform: "none",
              borderRadius: 2,
              py: 1.2,
              "&:hover": { background: "#196545" },
            }}
          >
            {t("happyHour.recapSetReminder")}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default HappyHourRecap;
